import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/app/Common";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Clock,
  MessageSquare,
  ClipboardCheck,
  ScrollText,
  Megaphone,
  Users,
  CalendarRange,
  GripVertical,
  ArrowRight,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";

const TOOLS = {
  attendance: { title: "Attendance", icon: Clock, to: "/app/attendance", desc: "GPS clock in / out + timesheet" },
  chat: { title: "Chat", icon: MessageSquare, to: "/app/chat", desc: "Live jobsite messaging" },
  inspections: { title: "Inspections", icon: ClipboardCheck, to: "/app/inspections", desc: "ITP execution + sign-off" },
  logs: { title: "Daily Logs", icon: ScrollText, to: "/app/logs", desc: "Field notes + photos" },
  announcements: { title: "Announcements", icon: Megaphone, to: "/app/announcements", desc: "Company-wide notices" },
  team: { title: "Team", icon: Users, to: "/app/admin/users", desc: "Roster & approvals" },
  roster: { title: "My Shifts", icon: CalendarRange, to: "/app/my-shifts", desc: "Upcoming shifts + open board" },
};

// As fotos de perfil ficam no backend; o usuario guarda so o link curto.
const BACKEND_ORIGIN = "https://webapp-sitesync.onrender.com";
const resolveUrl = (url) => (url && url.startsWith("/") ? `${BACKEND_ORIGIN}${url}` : url);

const ITP_STATUSES = [
  { key: "Draft", color: "#71717a" },
  { key: "In Progress", color: "#f59e0b" },
  { key: "In Review", color: "#3b82f6" },
  { key: "Closed", color: "#22c55e" },
];

/* ---------- ajudantes ---------- */

function useNow(ms) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

function initialsOf(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function formatSince(clockIn, now) {
  const ms = now - Date.parse(clockIn);
  if (!clockIn || Number.isNaN(ms)) return "-";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function Avatar({ name, url, size = 32, ring = false }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.36)) };
  const ringCls = ring ? "ring-2 ring-card" : "";
  if (url && !broken) {
    return (
      <img
        src={resolveUrl(url)}
        alt={name || "User"}
        title={name}
        style={style}
        onError={() => setBroken(true)}
        className={`shrink-0 rounded-full object-cover bg-muted ${ringCls}`}
      />
    );
  }
  return (
    <div
      title={name}
      style={style}
      className={`grid shrink-0 place-items-center rounded-full bg-primary/15 font-semibold text-primary ${ringCls}`}
    >
      {initialsOf(name)}
    </div>
  );
}

/* ---------- pecas visuais ---------- */

function LiveStatus() {
  const now = new Date(useNow(1000));
  return (
    <div className="hidden items-center gap-3 rounded-sm border border-border bg-card px-3 py-2 font-mono text-xs md:flex">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--status-pass))] opacity-70" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--status-pass))]" />
      </span>
      <span className="uppercase tracking-widest text-muted-foreground">Online</span>
      <span className="h-4 w-px bg-border" />
      <span className="font-bold tabular-nums">{now.toLocaleTimeString()}</span>
      <span className="text-muted-foreground">
        {now.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}
      </span>
    </div>
  );
}

function Sparkline({ values, color = "hsl(var(--primary))" }) {
  if (!values || values.length === 0) return null;
  const n = values.length;
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => [n === 1 ? 50 : (i / (n - 1)) * 100, 28 - (v / max) * 24]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `0,30 ${line} 100,30`;
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="h-10 w-full" aria-hidden="true">
      <polygon points={area} fillOpacity="0.14" style={{ fill: color }} />
      <polyline
        points={line}
        fill="none"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{ stroke: color }}
      />
    </svg>
  );
}

function Delta({ today, previous }) {
  const diff = today - previous;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
      <Icon className="h-3 w-3" /> {diff > 0 ? "+" : ""}
      {diff} vs previous day
    </span>
  );
}

function Panel({ kicker, title, right, children, className = "" }) {
  return (
    <div className={`rounded-sm border border-border bg-card ${className}`}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <div className="label-overline">{kicker}</div>
          <div className="mt-1 text-base font-semibold tracking-tight">{title}</div>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const ACCENTS = {
  orange: {
    grad: "from-primary/20 via-primary/5 to-transparent",
    bar: "bg-primary",
    shadow: "shadow-[0_0_20px_-6px_hsl(var(--primary)/0.5)]",
  },
  pass: {
    grad: "from-[hsl(var(--status-pass))/0.2] via-[hsl(var(--status-pass))/0.05] to-transparent",
    bar: "bg-[hsl(var(--status-pass))]",
    shadow: "shadow-[0_0_20px_-6px_hsl(var(--status-pass)/0.5)]",
  },
  hold: {
    grad: "from-[hsl(var(--status-hold))/0.2] via-[hsl(var(--status-hold))/0.05] to-transparent",
    bar: "bg-[hsl(var(--status-hold))]",
    shadow: "shadow-[0_0_20px_-6px_hsl(var(--status-hold)/0.5)]",
  },
};

function KpiCard({ label, value, accent = "orange", mono = true, capitalize = false, testId, className = "", hint, children }) {
  const a = ACCENTS[accent] || ACCENTS.orange;
  const shown = value === undefined || value === null || value === "" ? "-" : value;
  return (
    <div
      className={`relative overflow-hidden rounded-sm border border-border bg-card p-5 transition-transform hover:-translate-y-0.5 ${a.shadow} ${className}`}
      data-testid={testId}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${a.grad}`} />
      <div className={`absolute bottom-0 left-0 top-0 w-[3px] ${a.bar}`} />
      <div className="relative">
        <div className="flex items-center justify-between gap-2">
          <div className="label-overline">{label}</div>
          {hint}
        </div>
        <div
          className={`mt-2 text-3xl font-semibold tracking-tight md:text-4xl ${mono ? "font-mono" : ""} ${
            capitalize ? "capitalize" : ""
          }`}
        >
          {shown}
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Onsite Now com fotos ---------- */

function OnsiteStrip({ users, canOpen, onOpen, loaded, total }) {
  if (!loaded) return null;
  if (!users || users.length === 0) {
    return (
      <div className="mt-4 text-xs text-muted-foreground">
        {total > 0 ? "Crew details are visible to supervisors and admins." : "Nobody is clocked in right now."}
      </div>
    );
  }
  const MAX = 7;
  const shownUsers = users.slice(0, MAX);
  const extra = users.length - shownUsers.length;
  const names = users.slice(0, 2).map((u) => (u.name || "").split(" ")[0]);
  const label =
    users.length === 1
      ? names[0]
      : users.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names[0]}, ${names[1]} and ${users.length - 2} more`;

  const content = (
    <div className="mt-4 flex items-center gap-3">
      <div className="flex -space-x-2">
        {shownUsers.map((u) => (
          <Avatar key={u.id} name={u.name} url={u.avatar_url} size={36} ring />
        ))}
        {extra > 0 && (
          <div className="relative grid h-9 w-9 place-items-center rounded-full bg-muted font-mono text-[11px] font-bold ring-2 ring-card">
            +{extra}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{label}</div>
      {canOpen && (
        <span className="inline-flex shrink-0 items-center gap-0.5 font-mono text-[11px] uppercase tracking-widest text-primary">
          View crew <ChevronRight className="h-3 w-3" />
        </span>
      )}
    </div>
  );

  if (!canOpen) return content;
  return (
    <button type="button" onClick={onOpen} className="block w-full text-left" data-testid="onsite-open-crew">
      {content}
    </button>
  );
}

function CrewDialog({ users, isAdmin, onClose }) {
  const now = useNow(30000);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle>On site now</DialogTitle>
          <DialogDescription>
            {users.length} {users.length === 1 ? "person is" : "people are"} clocked in.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] divide-y divide-border overflow-y-auto">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 py-3">
              <Avatar name={u.name} url={u.avatar_url} size={40} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{u.name}</div>
                <div className="truncate text-xs text-muted-foreground">{u.jobsite_name || "-"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-xs font-bold">{formatSince(u.clock_in, now)}</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  since{" "}
                  {u.clock_in
                    ? new Date(u.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "-"}
                </div>
              </div>
            </div>
          ))}
        </div>

        {isAdmin && (
          <Link
            to="/app/admin/time-tracking"
            onClick={onClose}
            className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-primary hover:underline"
          >
            Open time tracking <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- graficos ---------- */

function ActivityChart({ data, loaded }) {
  if (!loaded) {
    return <div className="grid h-44 place-items-center font-mono text-xs text-muted-foreground">Loading...</div>;
  }
  if (!data || data.length === 0) {
    return <div className="grid h-44 place-items-center font-mono text-xs text-muted-foreground">No activity yet.</div>;
  }
  const max = Math.max(1, ...data.map((d) => Math.max(d.clock_ins, d.logs)));
  const barH = (v) => (v ? `${Math.max(8, (v / max) * 100)}%` : "2px");
  const dayLabel = (iso) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });

  return (
    <div>
      <div
        className="flex h-36 items-end gap-2"
        style={{
          backgroundImage: "linear-gradient(hsl(var(--border) / 0.7) 1px, transparent 1px)",
          backgroundSize: "100% 25%",
        }}
      >
        {data.map((d) => (
          <div
            key={d.date}
            className="flex h-full flex-1 items-end justify-center gap-1"
            title={`${d.date}: ${d.clock_ins} clock ins, ${d.logs} logs`}
          >
            <div className="w-3 rounded-t-sm bg-primary md:w-4" style={{ height: barH(d.clock_ins) }} />
            <div
              className="w-3 rounded-t-sm md:w-4"
              style={{ height: barH(d.logs), backgroundColor: "hsl(var(--status-pass))" }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        {data.map((d) => (
          <div
            key={d.date}
            className="flex-1 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            {dayLabel(d.date)}
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-5 font-mono text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary" /> Clock ins
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "hsl(var(--status-pass))" }} /> Daily logs
        </span>
      </div>
    </div>
  );
}

function ItpDonut({ status, loaded }) {
  const path = "M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831";
  const total = ITP_STATUSES.reduce((acc, s) => acc + ((status && status[s.key]) || 0), 0);
  let offset = 0;
  const segs = ITP_STATUSES.map((s) => {
    const n = (status && status[s.key]) || 0;
    const pct = total ? (n / total) * 100 : 0;
    const seg = { ...s, n, pct, offset };
    offset += pct;
    return seg;
  });

  if (!loaded) {
    return <div className="grid h-44 place-items-center font-mono text-xs text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row lg:flex-col xl:flex-row">
      <div className="relative h-32 w-32 shrink-0">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
          <path
            stroke="currentColor"
            style={{ color: "hsl(var(--border))" }}
            strokeWidth="3.8"
            fill="none"
            d={path}
          />
          {segs.map(
            (s) =>
              s.n > 0 && (
                <path
                  key={s.key}
                  fill="none"
                  strokeWidth="3.8"
                  d={path}
                  strokeDasharray={`${s.pct} 100`}
                  strokeDashoffset={`${-s.offset}`}
                  style={{ stroke: s.color }}
                />
              )
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-2xl font-bold">{total}</span>
          <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Total ITPs</span>
        </div>
      </div>

      <ul className="w-full space-y-2 font-mono text-xs">
        {segs.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.key}
            </span>
            <span className="font-bold">{s.n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- pagina ---------- */

export default function Dashboard() {
  const { user } = useAuth();
  const { activeSite } = useJobsite();
  const [order, setOrder] = useState(Object.keys(TOOLS));
  const [summary, setSummary] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [crewOpen, setCrewOpen] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "supervisor" || !!user?.is_master_admin;

  useEffect(() => {
    api
      .get("/prefs")
      .then(({ data }) => {
        if (Array.isArray(data.tools_order) && data.tools_order.length) {
          setOrder(data.tools_order.filter((k) => TOOLS[k]));
        }
      })
      .catch(() => {});
    api
      .get("/dashboard/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  }, []);

  const persistOrder = (next) => {
    setOrder(next);
    api.put("/prefs", { tools_order: next }).catch(() => {});
  };

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const next = [...order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setDragIdx(i);
    setOrder(next);
  };
  const onDragEnd = () => {
    setDragIdx(null);
    persistOrder(order);
  };

  const loaded = summary !== null;
  const activity = (summary && summary.activity_7d) || [];
  const logsSeries = activity.map((d) => d.logs);
  const lastDay = activity.length ? activity[activity.length - 1] : null;
  const prevDay = activity.length > 1 ? activity[activity.length - 2] : null;
  const openItps = summary ? (summary.open_itps ?? summary.itp_open) : undefined;
  const itpStatus = (summary && summary.itp_status) || {};
  const itpTotal = ITP_STATUSES.reduce((acc, s) => acc + (itpStatus[s.key] || 0), 0);
  const itpClosed = itpStatus.Closed || 0;
  const onsiteUsers = (summary && summary.onsite_users) || [];

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10">
      <PageHeader
        title={`Welcome, ${user?.name?.split(" ")[0] || "there"}.`}
        subtitle={activeSite ? `Working out of ${activeSite.name}.` : "Pick a jobsite in the header to scope your work."}
        action={<LiveStatus />}
      />

      {/* Indicadores principais */}
      <div className="mb-10 space-y-4" data-testid="dashboard-stats">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard
            label="Onsite Now"
            value={summary?.onsite}
            accent="pass"
            testId="stat-onsite"
            className="col-span-2"
            hint={
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--status-pass))]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--status-pass))] opacity-70" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-pass))]" />
                </span>
                Live
              </span>
            }
          >
            <OnsiteStrip
              users={onsiteUsers}
              canOpen={isAdmin && onsiteUsers.length > 0}
              onOpen={() => setCrewOpen(true)}
              loaded={loaded}
              total={summary?.onsite || 0}
            />
          </KpiCard>

          <KpiCard label="Open ITPs" value={openItps} accent="hold" testId="stat-itps">
            {loaded && itpTotal > 0 ? (
              <>
                <div className="mt-4 flex h-2 w-full overflow-hidden rounded-sm bg-muted">
                  {ITP_STATUSES.map(
                    (s) =>
                      (itpStatus[s.key] || 0) > 0 && (
                        <div
                          key={s.key}
                          title={`${s.key}: ${itpStatus[s.key]}`}
                          style={{ width: `${((itpStatus[s.key] || 0) / itpTotal) * 100}%`, backgroundColor: s.color }}
                        />
                      )
                  )}
                </div>
                <div className="mt-2 font-mono text-[11px] text-muted-foreground">
                  {itpClosed} closed of {itpTotal}
                </div>
              </>
            ) : (
              <div className="mt-4 font-mono text-[11px] text-muted-foreground">{loaded ? "No ITPs yet" : ""}</div>
            )}
          </KpiCard>

          <KpiCard label="Logs (24h)" value={summary?.logs_today} accent="orange" testId="stat-logs">
            <div className="mt-2">
              <Sparkline values={logsSeries} color="hsl(var(--primary))" />
            </div>
            {lastDay && prevDay && <Delta today={lastDay.logs} previous={prevDay.logs} />}
          </KpiCard>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard label="Users" value={summary?.users} accent="orange" testId="stat-users" />
          <KpiCard label="Pending" value={summary?.pending_users} accent="hold" testId="stat-pending">
            {isAdmin && (summary?.pending_users || 0) > 0 && (
              <Link
                to="/app/admin/users"
                className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-primary hover:underline"
              >
                Review <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </KpiCard>
          <KpiCard label="Jobsites" value={summary?.jobsites} accent="orange" testId="stat-jobsites" />
          <KpiCard
            label="Role"
            value={user?.role || "-"}
            mono={false}
            capitalize
            accent="orange"
            testId="stat-role"
          />
        </div>
      </div>

      {/* Atividade e ITPs */}
      <div className="mb-10 grid gap-4 lg:grid-cols-3">
        <Panel kicker="Activity" title="Last 7 days" className="lg:col-span-2">
          <ActivityChart data={activity} loaded={loaded} />
        </Panel>
        <Panel
          kicker="Inspections"
          title="ITPs by status"
          right={
            <Link
              to="/app/inspections"
              className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-primary hover:underline"
            >
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <ItpDonut status={itpStatus} loaded={loaded} />
        </Panel>
      </div>

      {/* Ferramentas, reordenaveis */}
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="label-overline mb-1">Tools</div>
          <h2 className="text-xl font-semibold tracking-tight">Drag to reorder</h2>
        </div>
        <div className="hidden font-mono text-xs uppercase tracking-widest text-muted-foreground md:block">
          Layout is saved per user
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tools-grid">
        {order.map((key, i) => {
          const t = TOOLS[key];
          if (!t) return null;
          return (
            <div
              key={key}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDragEnd={onDragEnd}
              className={`group rounded-sm border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-[0_0_24px_-10px_hsl(var(--primary)/0.6)] ${
                dragIdx === i ? "opacity-60 ring-2 ring-primary" : ""
              }`}
              data-testid={`tool-card-${key}`}
            >
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-sm border border-border text-primary">
                  <t.icon className="h-5 w-5" />
                </div>
                <GripVertical className="drag-handle h-4 w-4 text-muted-foreground opacity-50 group-hover:opacity-100" />
              </div>
              <div className="mt-4">
                <div className="text-base font-semibold">{t.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{t.desc}</div>
              </div>
              <Link
                to={t.to}
                className="mt-5 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-primary hover:underline"
              >
                Open <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          );
        })}
      </div>

      {!activeSite && (
        <div className="mt-10">
          <EmptyState
            title="No jobsite selected"
            body="Ask your admin to create a jobsite, or if you're an admin, add one from the top-left dropdown."
            action={
              isAdmin && (
                <Link to="/app/admin/jobsites">
                  <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="empty-add-jobsite">
                    Add jobsite
                  </Button>
                </Link>
              )
            }
          />
        </div>
      )}

      {crewOpen && <CrewDialog users={onsiteUsers} isAdmin={isAdmin} onClose={() => setCrewOpen(false)} />}
    </div>
  );
}
